import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import { 
  FiMessageSquare, 
  FiTrash2, 
  FiArchive,
  FiArrowUp,
  FiArrowDown,
  FiUser
} from 'react-icons/fi';
import type { ChatSession } from '../../services/chatService';
import { sessionRoute } from '../../utils/sessionRoute';

type SortKey = 'lastActivity' | 'messageCount' | 'created' | 'personaName';

interface ChatSessionTableProps {
  sessions: ChatSession[];
  onDelete?: (sessionId: string) => void;
  onArchive?: (sessionId: string) => void;
  isLoading?: boolean;
  sortKey?: SortKey;
  sortDir?: 'asc' | 'desc';
  onSortKeyChange?: (key: SortKey) => void;
  onToggleSortDir?: () => void;
}

const ChatSessionTableRow = memo(({ 
  session, 
  onDelete, 
  onArchive,
  darkMode 
}: {
  session: ChatSession;
  onDelete?: (sessionId: string) => void;
  onArchive?: (sessionId: string) => void;
  darkMode: boolean;
}) => {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'idle':
        return 'bg-yellow-500';
      case 'archived':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(session.id);
  };
  
  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onArchive?.(session.id);
  };
  
  return (
    <tr 
      className={`
        border-b transition-colors duration-150 cursor-pointer
        ${darkMode 
          ? 'border-gray-700 hover:bg-gray-700/30' 
          : 'border-gray-200 hover:bg-gray-50'
        }
      `}
    >
      <td className="px-4 py-3">
        <div className="flex items-center space-x-3">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center
            ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'}
          `}>
            {session.persona_avatar ? (
              <img src={session.persona_avatar} alt={session.persona_name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <FiUser className="w-5 h-5" />
            )}
          </div>
          <div>
            <div className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              {session.persona_name}
              {session.is_trailhead && (
                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300">
                  Trailhead
                </span>
              )}
            </div>
            {session.persona_specialization && (
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {session.persona_specialization}
              </div>
            )}
          </div>
        </div>
      </td>
      
      <td className="px-4 py-3">
        <div className={`max-w-xs truncate text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {session.last_message || 'No messages yet'}
        </div>
      </td>
      
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-2">
          <div className={`w-2 h-2 rounded-full ${getStatusColor(session.status)}`} />
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {session.message_count}
          </span>
        </div>
      </td>
      
      <td className="px-4 py-3">
        <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {(() => {
            try {
              const date = new Date(session.last_activity);
              if (isNaN(date.getTime())) return 'Recently';
              return formatDistanceToNow(date, { addSuffix: true });
            } catch (e) {
              return 'Recently';
            }
          })()}
        </div>
      </td>
      
      <td className="px-4 py-3">
        <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {(() => {
            try {
              const date = new Date(session.created_at);
              if (isNaN(date.getTime())) return 'Unknown';
              return formatDistanceToNow(date, { addSuffix: true });
            } catch (e) {
              return 'Unknown';
            }
          })()}
        </div>
      </td>
      
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity md:opacity-100">
          <Link
            to={sessionRoute(session)}
            className={`
              p-1.5 rounded transition-colors
              ${darkMode 
                ? 'hover:bg-green-900/50 text-green-400' 
                : 'hover:bg-green-100 text-green-600'
              }
            `}
            title="Continue chat"
          >
            <FiMessageSquare className="w-4 h-4" />
          </Link>
          {onArchive && (
            <button
              onClick={handleArchive}
              className={`
                p-1.5 rounded transition-colors
                ${darkMode 
                  ? 'hover:bg-blue-900/50 text-blue-400' 
                  : 'hover:bg-blue-100 text-blue-600'
                }
              `}
              title="Archive session"
            >
              <FiArchive className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={handleDelete}
              className={`
                p-1.5 rounded transition-colors
                ${darkMode 
                  ? 'hover:bg-red-900/50 text-red-400' 
                  : 'hover:bg-red-100 text-red-600'
                }
              `}
              title="Delete session"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
});

export const ChatSessionTable: React.FC<ChatSessionTableProps> = ({
  sessions,
  onDelete,
  onArchive,
  isLoading,
  sortKey,
  sortDir,
  onSortKeyChange,
  onToggleSortDir
}) => {
  const { darkMode } = useTheme();
  
  const handleHeaderClick = (key: SortKey) => {
    if (sortKey === key) {
      onToggleSortDir?.();
    } else {
      onSortKeyChange?.(key);
    }
  };
  
  const SortIndicator = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return null;
    return sortDir === 'asc' ? (
      <FiArrowUp className="w-3 h-3 ml-1 opacity-70" />
    ) : (
      <FiArrowDown className="w-3 h-3 ml-1 opacity-70" />
    );
  };
  
  const HeaderCell = ({ 
    children, 
    sortKey: key, 
    align = 'left',
    className = ''
  }: { 
    children: React.ReactNode; 
    sortKey?: SortKey; 
    align?: 'left' | 'center' | 'right';
    className?: string;
  }) => {
    const isClickable = !!key;
    const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
    
    return (
      <th 
        className={`
          px-4 py-3 text-xs font-medium uppercase tracking-wider ${alignClass}
          ${isClickable ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors' : ''}
          ${darkMode ? 'text-gray-400' : 'text-gray-500'}
          ${className}
        `}
        onClick={() => key && handleHeaderClick(key)}
      >
        <div className={`flex items-center ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'}`}>
          {children}
          {key && <SortIndicator columnKey={key} />}
        </div>
      </th>
    );
  };
  
  if (isLoading) {
    return (
      <div className="w-full">
        {[...Array(5)].map((_, i) => (
          <div 
            key={i} 
            className={`
              animate-pulse flex items-center gap-4 p-4 border-b
              ${darkMode ? 'border-gray-700' : 'border-gray-200'}
            `}
          >
            <div className={`h-10 w-10 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-4 rounded w-1/3 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-4 rounded w-16 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-4 rounded w-24 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
        ))}
      </div>
    );
  }
  
  if (sessions.length === 0) {
    return (
      <div className={`
        text-center py-12 rounded-lg border-2 border-dashed
        ${darkMode 
          ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
          : 'bg-gray-50 border-gray-200 text-gray-500'
        }
      `}>
        <FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">No chat sessions found</p>
        <p className="text-sm">Start a new conversation to get started</p>
      </div>
    );
  }
  
  return (
    <div className={`
      overflow-hidden rounded-lg border shadow-sm
      ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}
    `}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`
            text-left text-xs font-medium uppercase tracking-wider
            ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}
          `}>
            <tr>
              <HeaderCell sortKey="personaName">Persona</HeaderCell>
              <HeaderCell>Last Message</HeaderCell>
              <HeaderCell align="center" sortKey="messageCount">Messages</HeaderCell>
              <HeaderCell sortKey="lastActivity">Last Activity</HeaderCell>
              <HeaderCell sortKey="created">Created</HeaderCell>
              <HeaderCell align="right">Actions</HeaderCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sessions.map((session) => (
              <ChatSessionTableRow
                key={session.id}
                session={session}
                onDelete={onDelete}
                onArchive={onArchive}
                darkMode={darkMode}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
